import { useState } from 'react'
import { FiX, FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi'
import {
  LibraryOverlay,
  LibraryModal,
  LibraryHeader,
  LibraryTabs,
  LibraryTabButton,
  LibraryBody,
  LibraryTable,
  LibraryTh,
  LibraryTd,
  LibraryFootRow,
  SmallButton,
  RemoveButton,
  MovingLoadsInfoNote,
} from './canvasStyles'
import { createPathSegment, createTrain, createTrainAxle, cascadeRemoveTrain } from './movingLoadsModel'
import { useOverlayClose } from './useOverlayClose'

export function MovingLoadsPanel({
  elements,
  path,
  trains,
  combinations = [],
  setPath,
  setTrains,
  setCombinations,
  formErrors,
  onClose,
}) {
  const [tab, setTab] = useState('path')
  const [editingTrainKey, setEditingTrainKey] = useState(trains[0]?.key ?? null)

  const elementIds = elements.map((e) => e.id).filter((id) => id !== '')
  const editingTrain = trains.find((t) => t.key === editingTrainKey) || null

  function addSegment() {
    setPath((prev) => ({ ...prev, segments: [...prev.segments, createPathSegment(prev.segments)] }))
  }
  function updateSegment(key, field, value) {
    setPath((prev) => ({
      ...prev,
      segments: prev.segments.map((s) => (s.key === key ? { ...s, [field]: value } : s)),
    }))
  }
  function removeSegment(key) {
    setPath((prev) => ({ ...prev, segments: prev.segments.filter((s) => s.key !== key) }))
  }
  function moveSegment(index, direction) {
    setPath((prev) => {
      const segments = [...prev.segments]
      const target = index + direction
      if (target < 0 || target >= segments.length) return prev
      ;[segments[index], segments[target]] = [segments[target], segments[index]]
      return { ...prev, segments }
    })
  }
  function updatePathSampling(field, value) {
    setPath((prev) => ({ ...prev, sampling: { ...prev.sampling, [field]: value } }))
  }

  function addTrain() {
    const train = createTrain(trains)
    setTrains((prev) => [...prev, train])
    setEditingTrainKey(train.key)
  }
  function updateTrain(key, field, value) {
    const previous = trains.find((t) => t.key === key)
    setTrains((prev) => prev.map((t) => (t.key === key ? { ...t, [field]: value } : t)))
    if (field === 'id' && previous?.id && previous.id !== value && setCombinations) {
      setCombinations((prev) =>
        prev.map((c) => {
          if (!(previous.id in (c.trainFactors || {}))) return c
          const trainFactors = { ...c.trainFactors }
          const factor = trainFactors[previous.id]
          delete trainFactors[previous.id]
          if (value !== '' && !(value in trainFactors)) trainFactors[value] = factor
          return { ...c, trainFactors }
        })
      )
    }
  }
  function removeTrain(key) {
    const res = cascadeRemoveTrain(key, { trains, combinations })
    setTrains(res.trains)
    if (setCombinations) setCombinations(res.combinations)
    if (editingTrainKey === key) setEditingTrainKey(res.trains[0]?.key ?? null)
  }
  function addAxle(trainKey) {
    setTrains((prev) =>
      prev.map((t) => (t.key === trainKey ? { ...t, axles: [...t.axles, createTrainAxle(t.axles)] } : t))
    )
  }
  function updateAxle(trainKey, axleKey, field, value) {
    setTrains((prev) =>
      prev.map((t) =>
        t.key === trainKey ? { ...t, axles: t.axles.map((a) => (a.key === axleKey ? { ...a, [field]: value } : a)) } : t
      )
    )
  }
  function removeAxle(trainKey, axleKey) {
    setTrains((prev) =>
      prev.map((t) => (t.key === trainKey ? { ...t, axles: t.axles.filter((a) => a.key !== axleKey) } : t))
    )
  }

  const overlayCloseHandlers = useOverlayClose(onClose)
  return (
    <LibraryOverlay {...overlayCloseHandlers}>
      <LibraryModal onClick={(e) => e.stopPropagation()}>
        <LibraryHeader>
          <LibraryTabs>
            <LibraryTabButton type="button" $active={tab === 'path'} onClick={() => setTab('path')}>
              Caminho
            </LibraryTabButton>
            <LibraryTabButton type="button" $active={tab === 'trains'} onClick={() => setTab('trains')}>
              Trens de Carga
            </LibraryTabButton>
          </LibraryTabs>
          <button type="button" onClick={onClose} title="Fechar">
            <FiX />
          </button>
        </LibraryHeader>

        <LibraryBody>
          {trains.length > 0 && formErrors.length > 0 && (
            <MovingLoadsInfoNote>Corrija antes de calcular: {formErrors.join('; ')}</MovingLoadsInfoNote>
          )}

          {tab === 'path' && (
            <>
              <MovingLoadsInfoNote>
                Escolha por quais elementos a carga passa, na ordem do percurso. Se houver um trem-tipo configurado, o
                botão &quot;Calcular&quot; da barra de ferramentas já resolve normal, cortante e momento sob o efeito
                desse trem — não precisa configurar alvo ou carga manualmente.
              </MovingLoadsInfoNote>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Elemento</LibraryTh>
                    <LibraryTh>Inverter sentido</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {path.segments.map((s, index) => (
                    <tr key={s.key}>
                      <LibraryTd>
                        <select value={s.element} onChange={(e) => updateSegment(s.key, 'element', e.target.value)}>
                          <option value="">Elemento...</option>
                          {elementIds.map((id) => (
                            <option key={id} value={id}>
                              {id}
                            </option>
                          ))}
                        </select>
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          type="checkbox"
                          checked={!!s.reverse}
                          onChange={(e) => updateSegment(s.key, 'reverse', e.target.checked)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <SmallButton type="button" onClick={() => moveSegment(index, -1)} disabled={index === 0}>
                          <FiArrowUp />
                        </SmallButton>{' '}
                        <SmallButton
                          type="button"
                          onClick={() => moveSegment(index, 1)}
                          disabled={index === path.segments.length - 1}
                        >
                          <FiArrowDown />
                        </SmallButton>{' '}
                        <RemoveButton type="button" onClick={() => removeSegment(s.key)}>
                          <FiTrash2 />
                        </RemoveButton>
                      </LibraryTd>
                    </tr>
                  ))}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addSegment}>
                  <FiPlus /> Adicionar segmento
                </SmallButton>
              </LibraryFootRow>

              <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>Amostragem do caminho</h4>
              <LibraryTable>
                <tbody>
                  <tr>
                    <LibraryTd>
                      <select value={path.sampling.mode} onChange={(e) => updatePathSampling('mode', e.target.value)}>
                        <option value="samples">Nº de amostras</option>
                        <option value="step">Passo</option>
                      </select>
                    </LibraryTd>
                    <LibraryTd>
                      {path.sampling.mode === 'step' ? (
                        <input
                          type="number"
                          value={path.sampling.step}
                          placeholder="passo"
                          onChange={(e) => updatePathSampling('step', e.target.value)}
                        />
                      ) : (
                        <input
                          type="number"
                          min="2"
                          value={path.sampling.samples}
                          onChange={(e) => updatePathSampling('samples', e.target.value)}
                        />
                      )}
                    </LibraryTd>
                  </tr>
                </tbody>
              </LibraryTable>
            </>
          )}

          {tab === 'trains' && (
            <>
              <LibraryTable>
                <thead>
                  <tr>
                    <LibraryTh>Id</LibraryTh>
                    <LibraryTh>Nome</LibraryTh>
                    <LibraryTh>Componente</LibraryTh>
                    <LibraryTh>Sistema</LibraryTh>
                    <LibraryTh></LibraryTh>
                  </tr>
                </thead>
                <tbody>
                  {trains.map((t) => (
                    <tr key={t.key}>
                      <LibraryTd>
                        <input value={t.id} onChange={(e) => updateTrain(t.key, 'id', e.target.value)} />
                      </LibraryTd>
                      <LibraryTd>
                        <input
                          value={t.name}
                          placeholder="ex.: TB-450"
                          onChange={(e) => updateTrain(t.key, 'name', e.target.value)}
                        />
                      </LibraryTd>
                      <LibraryTd>
                        <select value={t.component} onChange={(e) => updateTrain(t.key, 'component', e.target.value)}>
                          <option value="Fx">Fx</option>
                          <option value="Fy">Fy</option>
                          <option value="Mz">Mz</option>
                        </select>
                      </LibraryTd>
                      <LibraryTd>
                        <select
                          value={t.coordinateSystem}
                          onChange={(e) => updateTrain(t.key, 'coordinateSystem', e.target.value)}
                        >
                          <option value="global">Global</option>
                          <option value="local">Local</option>
                        </select>
                      </LibraryTd>
                      <LibraryTd>
                        <SmallButton type="button" onClick={() => setEditingTrainKey(t.key)}>
                          Editar eixos
                        </SmallButton>{' '}
                        <RemoveButton type="button" onClick={() => removeTrain(t.key)}>
                          <FiTrash2 />
                        </RemoveButton>
                      </LibraryTd>
                    </tr>
                  ))}
                </tbody>
              </LibraryTable>
              <LibraryFootRow>
                <SmallButton type="button" onClick={addTrain}>
                  <FiPlus /> Adicionar trem
                </SmallButton>
              </LibraryFootRow>

              {editingTrain && (
                <>
                  <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>
                    Comprimento e cargas distribuídas do trem &quot;{editingTrain.id}&quot;
                  </h4>
                  <MovingLoadsInfoNote>
                    Opcional: além dos eixos, o trem pode carregar uma carga distribuída própria — &quot;interna&quot;
                    atua entre a posição de referência e a posição + comprimento do trem; &quot;externa&quot; atua no
                    restante do caminho. O comprimento é obrigatório se qualquer uma das duas for usada.
                  </MovingLoadsInfoNote>
                  <LibraryTable>
                    <thead>
                      <tr>
                        <LibraryTh>Comprimento do trem</LibraryTh>
                        <LibraryTh>Carga distribuída interna</LibraryTh>
                        <LibraryTh>Carga distribuída externa</LibraryTh>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <LibraryTd>
                          <input
                            type="number"
                            min="0"
                            value={editingTrain.length}
                            placeholder="ex.: 6"
                            onChange={(e) => updateTrain(editingTrain.key, 'length', e.target.value)}
                          />
                        </LibraryTd>
                        <LibraryTd>
                          <input
                            type="number"
                            value={editingTrain.insideDistributedLoad}
                            placeholder="ex.: -5"
                            onChange={(e) => updateTrain(editingTrain.key, 'insideDistributedLoad', e.target.value)}
                          />
                        </LibraryTd>
                        <LibraryTd>
                          <input
                            type="number"
                            value={editingTrain.outsideDistributedLoad}
                            placeholder="ex.: -10"
                            onChange={(e) => updateTrain(editingTrain.key, 'outsideDistributedLoad', e.target.value)}
                          />
                        </LibraryTd>
                      </tr>
                    </tbody>
                  </LibraryTable>

                  <h4 style={{ marginTop: '20px', marginBottom: '8px' }}>
                    Eixos do trem &quot;{editingTrain.id}&quot;
                  </h4>
                  <LibraryTable>
                    <thead>
                      <tr>
                        <LibraryTh>Offset acumulado</LibraryTh>
                        <LibraryTh>Valor</LibraryTh>
                        <LibraryTh></LibraryTh>
                      </tr>
                    </thead>
                    <tbody>
                      {editingTrain.axles.map((a) => (
                        <tr key={a.key}>
                          <LibraryTd>
                            <input
                              type="number"
                              min="0"
                              value={a.offset}
                              onChange={(e) => updateAxle(editingTrain.key, a.key, 'offset', e.target.value)}
                            />
                          </LibraryTd>
                          <LibraryTd>
                            <input
                              type="number"
                              value={a.value}
                              onChange={(e) => updateAxle(editingTrain.key, a.key, 'value', e.target.value)}
                            />
                          </LibraryTd>
                          <LibraryTd>
                            <RemoveButton type="button" onClick={() => removeAxle(editingTrain.key, a.key)}>
                              <FiTrash2 />
                            </RemoveButton>
                          </LibraryTd>
                        </tr>
                      ))}
                    </tbody>
                  </LibraryTable>
                  <LibraryFootRow>
                    <SmallButton type="button" onClick={() => addAxle(editingTrain.key)}>
                      <FiPlus /> Adicionar eixo
                    </SmallButton>
                  </LibraryFootRow>
                </>
              )}
            </>
          )}
        </LibraryBody>
      </LibraryModal>
    </LibraryOverlay>
  )
}
